/**
 * Avtalearket over API-et (C3, sak 274). Et ark per sak, versjoner som aldri endres, og diffen mot
 * forrige versjon regnet på serveren med `diffDealTerms`, så nettleser og mobil viser det samme.
 */
import { z } from 'zod';

import { dealTermsInputSchema, dealTermsSchema } from './deal-terms.js';
import { wireInstant } from '../contracts/wire-time.js';

export const DEAL_SHEET_STATUSES = ['open', 'accepted', 'declined', 'withdrawn'] as const;
export const dealSheetStatusSchema = z.enum(DEAL_SHEET_STATUSES).meta({ id: 'DealSheetStatus' });
export type DealSheetStatus = (typeof DEAL_SHEET_STATUSES)[number];

export const DEAL_VERSION_STATUSES = [
  'proposed',
  'accepted',
  'declined',
  'superseded',
  'withdrawn',
] as const;
export const dealVersionStatusSchema = z
  .enum(DEAL_VERSION_STATUSES)
  .meta({ id: 'DealVersionStatus' });
export type DealVersionStatus = (typeof DEAL_VERSION_STATUSES)[number];

/** `owner` er arrangøren (festival eller arrangør som leier), `counterparty` byrået eller spillestedet. */
export const DEAL_SIDES = ['owner', 'counterparty'] as const;
export const dealSideSchema = z.enum(DEAL_SIDES).meta({ id: 'DealSide' });
export type DealSide = (typeof DEAL_SIDES)[number];

export const DEAL_SUBJECT_KINDS = ['engagement', 'date_request'] as const;
export type DealSubjectKind = (typeof DEAL_SUBJECT_KINDS)[number];

/** «Godta og gå til signering» eller «Godta, kontrakt senere» (274.7). */
export const DEAL_ACCEPT_MODES = ['sign_now', 'sign_later'] as const;
export const dealAcceptModeSchema = z.enum(DEAL_ACCEPT_MODES).meta({ id: 'DealAcceptMode' });
export type DealAcceptMode = (typeof DEAL_ACCEPT_MODES)[number];

export const dealTermsChangeDtoSchema = z
  .object({
    key: z.string(),
    path: z.string(),
    // Et punkt som ikke var avtalt før (eller er tatt bort) har ingen verdi; JSON dropper den.
    before: z.unknown().optional(),
    after: z.unknown().optional(),
  })
  .meta({ id: 'DealTermsChange' });

export const dealSheetVersionDtoSchema = z
  .object({
    id: z.uuid(),
    versionNumber: z.number().int(),
    authorSide: dealSideSchema,
    authorOrganizationName: z.string().nullable(),
    authorPersonName: z.string().nullable(),
    /** Skrevet av huset som spør. */
    mine: z.boolean(),
    status: dealVersionStatusSchema,
    terms: dealTermsSchema,
    /** Endringene fra forrige versjon, blad for blad. Første versjon: alt som er fylt ut. */
    changes: z.array(dealTermsChangeDtoSchema),
    message: z.string().nullable(),
    createdAt: wireInstant,
    decidedAt: wireInstant.nullable(),
  })
  .meta({ id: 'DealSheetVersion' });
export type DealSheetVersionDto = z.infer<typeof dealSheetVersionDtoSchema>;

export const dealSheetDtoSchema = z
  .object({
    id: z.uuid(),
    subjectKind: z.enum(DEAL_SUBJECT_KINDS),
    status: dealSheetStatusSchema,
    /** Siden huset som spør står på. */
    ourSide: dealSideSchema,
    ownerName: z.string().nullable(),
    counterpartyName: z.string().nullable(),
    /** Byrået eller spillestedet er i Tvitvi; ellers fører arrangøren svarene selv. */
    counterpartyInTvitvi: z.boolean(),
    artistName: z.string().nullable(),
    title: z.string().nullable(),
    /** Husets egen inngang til saken: budet hos arrangøren, innboksraden hos motparten. */
    bookingBidId: z.uuid().nullable(),
    bookingEnquiryId: z.uuid().nullable(),
    dateRequestId: z.uuid().nullable(),
    venueEnquiryId: z.uuid().nullable(),
    currentVersionId: z.uuid().nullable(),
    acceptedVersionId: z.uuid().nullable(),
    acceptedMode: dealAcceptModeSchema.nullable(),
    acceptedAt: wireInstant.nullable(),
    /** Beløpene er tatt ut av vilkårene fordi leseren ikke har «Se priser og honorar». */
    amountsHidden: z.boolean(),
    /** Den siste versjonen venter på svar fra oss. */
    canAnswer: z.boolean(),
    canCounter: z.boolean(),
    canWithdraw: z.boolean(),
    /**
     * A8 (279.3 a): den godtatte avtalen kan endres direkte av denne parten. Endringen blir en ny
     * godtatt versjon, og den andre parten får varsel. Usann når en kontrakt er signert eller ute til
     * signering — se `lockedBy`.
     */
    canAmend: z.boolean().optional(),
    /** Kontrakten som låser avtalen: signert (endres med en ny kontraktversjon) eller ute til signering. */
    lockedBy: z
      .object({
        contractId: z.uuid(),
        number: z.string(),
        state: z.enum(['signed', 'signing']),
      })
      .nullable()
      .optional(),
    /**
     * HVOR AVTALEN GJELDER (QA15 B7, funn 19), lest av saken: en sal hos et spillested, en konsert
     * eller en festival. Skjemaet viser avtaletypene for de to første og ingen scene for en sal.
     */
    placement: z.enum(['festival', 'venue', 'concert']).optional(),
    /**
     * QA16 funn 26 (309.1): leiearket ble godtatt av spillestedet i det datoen ble bekreftet, med
     * spillestedets egen versjon. Begge parter ser linjen om det.
     */
    confirmedByHouse: z.boolean().optional(),
    versions: z.array(dealSheetVersionDtoSchema),
  })
  .meta({ id: 'DealSheet' });
export type DealSheetDto = z.infer<typeof dealSheetDtoSchema>;

export const dealSheetLookupQuerySchema = z
  .object({
    bidId: z.uuid().optional(),
    enquiryId: z.uuid().optional(),
    dateRequestId: z.uuid().optional(),
    venueEnquiryId: z.uuid().optional(),
  })
  .refine(
    (q) =>
      [q.bidId, q.enquiryId, q.dateRequestId, q.venueEnquiryId].filter((v) => v !== undefined)
        .length === 1,
    { message: 'Give exactly one of bidId, enquiryId, dateRequestId, venueEnquiryId.' },
  )
  .meta({ id: 'DealSheetLookupQuery' });
export type DealSheetLookupQuery = z.infer<typeof dealSheetLookupQuerySchema>;

/** Leiearkene bak konsertens datoforespørsler, i ett kall (G2: ett oppslag per rad før). */
export const dealSheetsForDateRequestsQuerySchema = z
  .object({
    dateRequestIds: z.preprocess((value: unknown): unknown => {
      if (value === undefined || value === null) return undefined;
      if (Array.isArray(value)) return value;
      const text = String(value).trim();
      return text.length === 0 ? undefined : text.split(',');
    }, z.array(z.uuid()).min(1).max(100)),
  })
  .meta({ id: 'DealSheetsForDateRequestsQuery' });
export type DealSheetsForDateRequestsQuery = z.infer<typeof dealSheetsForDateRequestsQuerySchema>;

export const dealSheetListResponseSchema = z
  .object({ items: z.array(dealSheetDtoSchema) })
  .meta({ id: 'DealSheetListResponse' });
export type DealSheetListResponse = z.infer<typeof dealSheetListResponseSchema>;

export const proposeDealVersionRequestSchema = z
  .object({
    /** Versjonen motbudet svarer på. En annen siste versjon gir 409: noen svarte først. */
    basedOnVersionId: z.uuid(),
    terms: dealTermsInputSchema,
    message: z.string().trim().max(4000).optional(),
  })
  .meta({ id: 'ProposeDealVersionRequest' });
export type ProposeDealVersionRequest = z.infer<typeof proposeDealVersionRequestSchema>;

/**
 * A8: ENDRE DEN GODTATTE AVTALEN (279.3 a). Samme form som et motbud, men versjonen blir gjeldende med
 * en gang. `basedOnVersionId` er den godtatte versjonen endringen bygger på.
 */
export const amendDealSheetRequestSchema = proposeDealVersionRequestSchema
  .extend({})
  .meta({ id: 'AmendDealSheetRequest' });
export type AmendDealSheetRequest = z.infer<typeof amendDealSheetRequestSchema>;

export const acceptDealVersionRequestSchema = z
  .object({
    versionId: z.uuid(),
    mode: dealAcceptModeSchema,
    /**
     * QA16 funn 3: spillestedet har sett at noen står foran i køen og godtar likevel. Uten den svarer
     * et leieark som ville gått forbi køen 409 `queue_ahead`.
     */
    passQueue: z.boolean().optional(),
  })
  .meta({ id: 'AcceptDealVersionRequest' });
export type AcceptDealVersionRequest = z.infer<typeof acceptDealVersionRequestSchema>;

export const declineDealVersionRequestSchema = z
  .object({ versionId: z.uuid(), message: z.string().trim().max(4000).optional() })
  .meta({ id: 'DeclineDealVersionRequest' });
export type DeclineDealVersionRequest = z.infer<typeof declineDealVersionRequestSchema>;

/* ------------------------------------------------------------------------- vilkårslista */

export const dealStandardTermDtoSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    version: z.string(),
    body: z.string(),
    isDefault: z.boolean(),
    sortOrder: z.number().int(),
  })
  .meta({ id: 'DealStandardTerm' });
export type DealStandardTermDto = z.infer<typeof dealStandardTermDtoSchema>;

export const dealStandardTermListResponseSchema = z
  .object({ items: z.array(dealStandardTermDtoSchema) })
  .meta({ id: 'DealStandardTermListResponse' });
export type DealStandardTermListResponse = z.infer<typeof dealStandardTermListResponseSchema>;

export const createDealStandardTermRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    version: z.string().trim().min(1).max(40).optional(),
    body: z.string().max(20000).optional(),
    isDefault: z.boolean().optional(),
  })
  .meta({ id: 'CreateDealStandardTermRequest' });
export type CreateDealStandardTermRequest = z.infer<typeof createDealStandardTermRequestSchema>;

export const updateDealStandardTermRequestSchema = createDealStandardTermRequestSchema
  .partial()
  .meta({ id: 'UpdateDealStandardTermRequest' });
export type UpdateDealStandardTermRequest = z.infer<typeof updateDealStandardTermRequestSchema>;
